import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { applyDensity, readStoredDensity, writeStoredDensity, type Density } from './density';

export interface DensityContextValue {
  density: Density;
  setDensity: (next: Density) => void;
}

const DensityContext = createContext<DensityContextValue | null>(null);

/**
 * Density state, `localStorage`-backed — deliberately a much smaller provider than `ThemeProvider`.
 *
 * There is no `PATCH /v1/profile` write-through and no pending-sync marker, because there is no
 * backend field to write to (see `density.ts`). That absence is the whole difference between the
 * two providers, and it is why this one has no retry path, no hydrate entry point, and no
 * overlapping-write hazard to defend against.
 */
export function DensityProvider({ children }: { children: ReactNode }) {
  const [density, setDensityState] = useState<Density>(() => readStoredDensity());

  // Applies and persists on every change, including on mount — the same belt-and-braces as
  // `ThemeProvider`, even though `index.html`'s bootstrap has already applied the stored value
  // synchronously before React mounted.
  useEffect(() => {
    applyDensity(density);
    writeStoredDensity(density);
  }, [density]);

  const setDensity = useCallback((next: Density) => {
    setDensityState(next);
  }, []);

  return (
    <DensityContext.Provider value={{ density, setDensity }}>{children}</DensityContext.Provider>
  );
}

export function useDensity(): DensityContextValue {
  const context = useContext(DensityContext);
  if (context === null) {
    throw new Error('useDensity must be used within a DensityProvider');
  }
  return context;
}
